import { Campus, Child, Photo } from "@/types";
import { Order, Package } from "@/types/api";
import React, { createContext, ReactNode, useContext, useReducer } from "react";

export interface PhotoFolder {
  id: string;
  name: string;
  date: string;
  photoCount: number;
  createdBy?: string;
  dateModified?: string;
}

// Application state interface
interface AppState {
  currentView: "centers" | "center" | "folder";
  selectedCampusId: string | null;
  selectedChildId: string | null;
  selectedFolderId: string | null;
  campuses: Campus[];
  children: Record<string, Child[]>;
  folders: Record<string, PhotoFolder[]>;
  photos: Record<string, Photo[]>;
  orders: Record<string, Order[]>;
  packages: Package[];
}

// Action types
type AppAction =
  | { type: "SET_VIEW"; payload: "centers" | "center" | "folder" }
  | { type: "SELECT_CAMPUS"; payload: string }
  | { type: "SELECT_CHILD"; payload: string }
  | { type: "SELECT_FOLDER"; payload: string }
  | { type: "SET_CAMPUSES"; payload: Campus[] }
  | { type: "SET_CHILDREN"; payload: { campusId: string; children: Child[] } }
  | {
      type: "SET_FOLDERS";
      payload: { childId: string; folders: PhotoFolder[] };
    }
  | { type: "ADD_FOLDER"; payload: { childId: string; folder: PhotoFolder } }
  | { type: "SET_PHOTOS"; payload: { folderId: string; photos: Photo[] } }
  | { type: "ADD_PHOTOS"; payload: { folderId: string; photos: Photo[] } }
  | { type: "DELETE_PHOTO"; payload: { folderId: string; photoId: string } }
  | { type: "SET_ORDERS"; payload: { folderId: string; orders: Order[] } }
  | { type: "SET_PACKAGES"; payload: Package[] }
  | { type: "RESET_SELECTIONS" };

// Initial state
const initialState: AppState = {
  currentView: "centers",
  selectedCampusId: null,
  selectedChildId: null,
  selectedFolderId: null,
  campuses: [],
  children: {},
  folders: {},
  photos: {},
  orders: {},
  packages: [],
};

// Reducer function
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case "SET_VIEW":
      return { ...state, currentView: action.payload };

    case "SELECT_CAMPUS":
      return {
        ...state,
        selectedCampusId: action.payload,
        selectedChildId: null,
        selectedFolderId: null,
      };

    case "SELECT_CHILD":
      return {
        ...state,
        selectedChildId: action.payload,
        selectedFolderId: null,
      };

    case "SELECT_FOLDER":
      return { ...state, selectedFolderId: action.payload };

    case "SET_CAMPUSES":
      return { ...state, campuses: action.payload };

    case "SET_CHILDREN":
      return {
        ...state,
        children: {
          ...state.children,
          [action.payload.campusId]: action.payload.children,
        },
      };

    case "SET_FOLDERS":
      return {
        ...state,
        folders: {
          ...state.folders,
          [action.payload.childId]: action.payload.folders,
        },
      };

    case "ADD_FOLDER":
      return {
        ...state,
        folders: {
          ...state.folders,
          [action.payload.childId]: [
            ...(state.folders[action.payload.childId] || []),
            action.payload.folder,
          ],
        },
      };

    case "SET_PHOTOS":
      return {
        ...state,
        photos: {
          ...state.photos,
          [action.payload.folderId]: action.payload.photos,
        },
      };

    case "ADD_PHOTOS":
      return {
        ...state,
        photos: {
          ...state.photos,
          [action.payload.folderId]: [
            ...(state.photos[action.payload.folderId] || []),
            ...action.payload.photos,
          ],
        },
      };

    case "DELETE_PHOTO":
      return {
        ...state,
        photos: {
          ...state.photos,
          [action.payload.folderId]:
            state.photos[action.payload.folderId]?.filter(
              (photo) => photo.id !== action.payload.photoId
            ) || [],
        },
      };

    case "SET_ORDERS":
      return {
        ...state,
        orders: {
          ...state.orders,
          [action.payload.folderId]: action.payload.orders,
        },
      };

    case "SET_PACKAGES":
      return {
        ...state,
        packages: action.payload.sort((a, b) => a.name.localeCompare(b.name)),
      };

    case "RESET_SELECTIONS":
      return {
        ...state,
        selectedCampusId: null,
        selectedChildId: null,
        selectedFolderId: null,
        currentView: "centers",
      };

    default:
      return state;
  }
};

// Context creation
const AppStateContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

// Provider component
interface AppStateProviderProps {
  children: ReactNode;
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
};

// Hook to use the context
export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within an AppStateProvider");
  }
  return context;
};

// Convenience selectors
export const useSelectedCampus = () => {
  const { state } = useAppState();
  return state.campuses.find((campus) => campus.id === state.selectedCampusId);
};

export const useSelectedChild = () => {
  const { state } = useAppState();
  if (!state.selectedCampusId || !state.selectedChildId) return null;
  return state.children[state.selectedCampusId]?.find(
    (child) => child.childID === state.selectedChildId
  );
};

export const useSelectedFolder = () => {
  const { state } = useAppState();
  if (!state.selectedChildId || !state.selectedFolderId) return null;
  return state.folders[state.selectedChildId]?.find(
    (folder) => folder.id === state.selectedFolderId
  );
};
